import React, { createContext, useContext, useEffect, useState } from 'react';
import apiClient from '../services/api';
import { logger } from '../utils/logger';

// Workout type with color mapping
export interface WorkoutTypeWithColor {
  id: string;
  name: string;
  type: string;
  sportType: 'running' | 'cycling';
  color: {
    primary: string;
    secondary: string;
    mui: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default';
  };
}

// Color palette for workout types
const WORKOUT_COLORS = {
  REST: {
    primary: '#9E9E9E', // Grey 500
    secondary: '#F5F5F5', // Grey 100
    mui: 'default' as const,
  },
  RECOVERY: {
    primary: '#4CAF50', // Green 500
    secondary: '#E8F5E8', // Green 50
    mui: 'success' as const,
  },
  BASE: {
    primary: '#2196F3', // Blue 500
    secondary: '#E3F2FD', // Blue 50
    mui: 'primary' as const,
  },
  LONG_RUN: {
    primary: '#4CAF50', // Green 500 - Long workouts in green
    secondary: '#E8F5E8', // Green 50
    mui: 'success' as const,
  },
  BIKE_LONG_RIDE: {
    primary: '#4CAF50', // Green 500 - Long workouts in green
    secondary: '#E8F5E8', // Green 50
    mui: 'success' as const,
  },
  TEMPO: {
    primary: '#9C27B0', // Purple 500
    secondary: '#F3E5F5', // Purple 50
    mui: 'secondary' as const,
  },
  BIKE_TEMPO: {
    primary: '#9C27B0', // Purple 500 - Same as running Tempo
    secondary: '#F3E5F5', // Purple 50
    mui: 'secondary' as const,
  },
  THRESHOLD: {
    primary: '#F44336', // Red 500
    secondary: '#FFEBEE', // Red 50
    mui: 'error' as const,
  },
  BIKE_THRESHOLD: {
    primary: '#F44336', // Red 500 - Same as running Threshold
    secondary: '#FFEBEE', // Red 50
    mui: 'error' as const,
  },
  BIKE_FTP_INTERVALS: {
    primary: '#F44336', // Red 500 - Hard workout like Threshold
    secondary: '#FFEBEE', // Red 50
    mui: 'error' as const,
  },
  FTP_INTERVALS: {
    primary: '#F44336', // Red 500 - Hard workout like Threshold
    secondary: '#FFEBEE', // Red 50
    mui: 'error' as const,
  },
  SPRINT: {
    primary: '#E91E63', // Pink 500
    secondary: '#FCE4EC', // Pink 50
    mui: 'secondary' as const,
  },
  VO2MAX: {
    primary: '#FF5722', // Deep Orange 500
    secondary: '#FBE9E7', // Deep Orange 50
    mui: 'error' as const,
  },
  BENCHMARK: {
    primary: '#795548', // Brown 500
    secondary: '#EFEBE9', // Brown 50
    mui: 'default' as const,
  },
  BIKE_FTP_TEST: {
    primary: '#795548', // Brown 500
    secondary: '#EFEBE9', // Brown 50
    mui: 'default' as const,
  },
} as const;

interface WorkoutTypesState {
  workoutTypes: WorkoutTypeWithColor[];
  isLoading: boolean;
  error: string | null;
}

interface WorkoutTypesContextType extends WorkoutTypesState {
  getWorkoutTypeColor: (workoutName: string) => WorkoutTypeWithColor['color'];
  getWorkoutTypeById: (id: string) => WorkoutTypeWithColor | null;
  getWorkoutTypeByName: (name: string) => WorkoutTypeWithColor | null;
  refreshWorkoutTypes: () => Promise<void>;
}

const WorkoutTypesContext = createContext<WorkoutTypesContextType | undefined>(undefined);

export const useWorkoutTypes = (): WorkoutTypesContextType => {
  const context = useContext(WorkoutTypesContext);
  if (!context) {
    throw new Error('useWorkoutTypes must be used within a WorkoutTypesProvider');
  }
  return context;
};

interface WorkoutTypesProviderProps {
  children: React.ReactNode;
}

export const WorkoutTypesProvider: React.FC<WorkoutTypesProviderProps> = ({ children }) => {
  const [state, setState] = useState<WorkoutTypesState>({
    workoutTypes: [],
    isLoading: true,
    error: null,
  });

  const loadWorkoutTypes = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await apiClient.getWorkoutTypes();

      if (response.success && response.data) {
        // Map workout types with colors
        const workoutTypesWithColors: WorkoutTypeWithColor[] = response.data.map(
          (workoutType: {
            id: string;
            name: string;
            type: string;
            sportType: 'running' | 'cycling';
          }) => {
            // Strip RUN_ or BIKE_ prefix to match WORKOUT_COLORS keys
            const colorKey = workoutType.id.replace(/^(RUN_|BIKE_)/, '');
            // Also check with BIKE_ prefix for bike-specific colors
            const bikeColorKey = `BIKE_${colorKey}`;

            return {
              id: workoutType.id,
              name: workoutType.name,
              type: workoutType.type,
              sportType: workoutType.sportType,
              color:
                WORKOUT_COLORS[colorKey as keyof typeof WORKOUT_COLORS] ||
                WORKOUT_COLORS[bikeColorKey as keyof typeof WORKOUT_COLORS] ||
                WORKOUT_COLORS.BASE,
            };
          }
        );

        setState({
          workoutTypes: workoutTypesWithColors,
          isLoading: false,
          error: null,
        });

        logger.info(`Workout types loaded successfully: ${workoutTypesWithColors.length} types`);
      } else {
        throw new Error(response.error || 'Failed to load workout types');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load workout types';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      logger.error('Failed to load workout types', { error });
    }
  };

  const getWorkoutTypeColor = (workoutName: string): WorkoutTypeWithColor['color'] => {
    const workoutType = state.workoutTypes.find(
      type => type.name?.toLowerCase() === workoutName?.toLowerCase()
    );

    // If found, return its color
    if (workoutType) {
      return workoutType.color;
    }

    const lowerName = workoutName?.toLowerCase() ?? '';

    // Fallbacks
    if (lowerName.includes('long')) {
      return WORKOUT_COLORS.LONG_RUN; // Green color for long workouts
    }
    if (lowerName.includes('rest')) {
      return WORKOUT_COLORS.REST; // Grey color for rest days
    }
    if (lowerName.includes('endurance')) {
      return WORKOUT_COLORS.LONG_RUN; // Green color for endurance workouts
    }
    if (lowerName.includes('hard')) {
      return WORKOUT_COLORS.BIKE_THRESHOLD; // Grey color for hard workouts
    }

    // Hard bike workouts - match their running equivalents
    if (lowerName.includes('tempo') || lowerName.includes('sweet spot')) {
      return WORKOUT_COLORS.BIKE_TEMPO; // Purple for tempo/sweet spot
    }
    if (lowerName.includes('threshold') || lowerName.includes('ftp')) {
      return WORKOUT_COLORS.BIKE_THRESHOLD; // Red for threshold/FTP
    }
    if (lowerName.includes('vo2max')) {
      return WORKOUT_COLORS.VO2MAX; // Deep orange for VO2max
    }

    return WORKOUT_COLORS.BASE;
  };

  const getWorkoutTypeById = (id: string): WorkoutTypeWithColor | null => {
    return state.workoutTypes.find(type => type.id === id) || null;
  };

  const getWorkoutTypeByName = (name: string): WorkoutTypeWithColor | null => {
    return state.workoutTypes.find(type => type.name.toLowerCase() === name.toLowerCase()) || null;
  };

  const refreshWorkoutTypes = async () => {
    await loadWorkoutTypes();
  };

  // Load workout types on mount
  useEffect(() => {
    loadWorkoutTypes();
  }, []);

  const contextValue: WorkoutTypesContextType = {
    ...state,
    getWorkoutTypeColor,
    getWorkoutTypeById,
    getWorkoutTypeByName,
    refreshWorkoutTypes,
  };

  return (
    <WorkoutTypesContext.Provider value={contextValue}>{children}</WorkoutTypesContext.Provider>
  );
};

export default WorkoutTypesProvider;
